import ActionButton from "#/components/buttons/ActionButton";
import { TicketStatus, type CreateTicketPayload, type Ticket } from "#/types/tickets";
import { sanitize } from "#/utils/StringUtils";
import { useSortable, type AnimateLayoutChanges } from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Badge, Button, Card, Loader, Textarea } from "@mantine/core";
import { useForm } from "@mantine/form";
import { IconChevronDown, IconDeviceFloppy, IconX } from "@tabler/icons-react";
import { useClient } from "#/hook/useClient";
import { formatDateTime } from "#/utils/dateTimeUtils";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { usePermission } from "#/hooks/usePermission";
import PermissionTooltip from "#/components/ui/PermissionTooltip";

const KeyValueView = ({
  label,
  value,
}: {
  label: string;
  value: string | number;
}) => {
  return (
    <div className="w-full flex gap-1 text-xs">
      <span className=" font-bold">{label} :</span>
      <span className="font-medium">{value}</span>
    </div>
  );
};

export interface Props {
  isUpdating: boolean;
  ticket: Ticket;
  onMarkTicketAsProblem: (payload: Partial<CreateTicketPayload>) => void;
}

export const TicketCard = ({
  isUpdating,
  ticket,
  onMarkTicketAsProblem,
}: Props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { client } = useClient();
  const { canWrite } = usePermission();
  const animateLayoutChanges: AnimateLayoutChanges = (args) => {
    const { isSorting, wasDragging } = args;
    return isSorting || wasDragging;
  };

  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: ticket.id,
    animateLayoutChanges,
  });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const [expanded, setExpanded] = useState<boolean>(false);
  const [markAsProblem, setMarkAsProblem] = useState<boolean>(false);

  const form = useForm({
    initialValues: {
      problem_details: ticket.problem_details ?? "",
    },
    validate: {
      problem_details: (value) => (value ? null : t("tickets.card.reasonRequired")),
    },
  });

  const handleSubmit = (values: typeof form.values) => {
    onMarkTicketAsProblem({
      status: TicketStatus.ProblemTickets,
      ...values,
    });
  };

  return (
    <div className="px-4 py-0 cursor-pointer hover:scale-100 transition-all duration-200">
      <Card
        ref={setNodeRef}
        style={style}
        withBorder
        shadow="sm"
        radius="md"
        p="0"
        py={8}
        mb="sm"
        {...attributes}
        {...listeners}
        onClick={() => setExpanded((e) => !e)}
      >
        <div className="w-full flex flex-col pt-1 relative">
          <div className="w-full flex flex-col px-3">
            <div className="w-full flex items-center justify-between">
              <span className="text-2xs text-gray-400 font-bold">
                {t("tickets.card.ticketNumber", { id: ticket.id ?? "--" })}
              </span>
              {isUpdating && <Loader size={"12"} color="blue" />}
            </div>
            <span className="text-lf font-bold capitalize">
              {sanitize(ticket.task) ?? "--"}
            </span>
            <div className="flex flex-wrap gap-1 mt-1">
              {ticket.items && ticket.items.length > 0 && (
                ticket.items.flatMap((item) =>
                  item.rfid_tags && item.rfid_tags.length > 0
                    ? item.rfid_tags.map((tag) => (
                        <Badge
                          key={tag.id}
                          size="xs"
                          variant="light"
                          color="blue"
                          className="cursor-pointer hover:scale-105 transition-transform"
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate(`/inventory/items/${item.id}`);
                          }}
                        >
                          {tag.rfid}
                        </Badge>
                      ))
                    : (
                        <Badge
                          key={`item-${item.id}`}
                          size="xs"
                          variant="light"
                          color="gray"
                          className="cursor-pointer hover:scale-105 transition-transform"
                          onClick={(e) => {
                            e.stopPropagation();
                            void navigate(`/inventory/items/${item.id}`);
                          }}
                        >
                          {t("tickets.card.itemNumber", { id: item.id })}
                        </Badge>
                      )
                )
              )}

              {ticket.products && ticket.products.length > 0 && (
                ticket.products.map((product) => (
                  <Badge
                    key={`product-${product.id}`}
                    size="xs"
                    variant="light"
                    color="green"
                    className="cursor-pointer hover:scale-105 transition-transform"
                    onClick={(e) => {
                      e.stopPropagation();
                      void navigate(`/inventory/categories/${product.id}`);
                    }}
                  >
                    {product.name}
                  </Badge>
                ))
              )}

              {(!ticket.items || ticket.items.length === 0) &&
               (!ticket.products || ticket.products.length === 0) && (
                <span className="text-2xs font-medium text-gray-400">
                  {t("tickets.card.noItemsOrProducts")}
                </span>
              )}
            </div>
          </div>

          <div
            className={`w-full overflow-hidden mt-1 transition-all duration-300 ease-in-out ${
              expanded
                ? "max-h-[1000px] opacity-100 scale-100"
                : "max-h-0 opacity-0 scale-95 py-0"
            }`}
          >
            <div className="bg-gray-50 flex flex-col">
              <div className="w-full flex flex-col gap-1 border-y border-dashed border-y-gray-300 p-3">
                <KeyValueView label={t("tickets.card.id")} value={ticket.id} />
                <KeyValueView
                  label={t("tickets.card.date")}
                  value={formatDateTime(ticket.created_at, client)}
                />
                <KeyValueView
                  label={t("tickets.card.deadline")}
                  value={formatDateTime(ticket.deadline, client)}
                />
                <KeyValueView
                  label={t("tickets.card.personInCharge")}
                  value={ticket.person_in_charge}
                />
                <KeyValueView label={t("tickets.card.notify")} value={ticket.notify_email} />
                {ticket.status === TicketStatus.ProblemTickets && (
                  <KeyValueView
                    label={t("tickets.card.problemDetails")}
                    value={ticket.problem_details ?? "--"}
                  />
                )}
              </div>

              <form
                className={`flex flex-col transition-all duration-500 p-2 ${
                  markAsProblem && "bg-red-50/20 space-y-2"
                }`}
              >
                <div
                  className={`w-full overflow-hidden transition-all duration-500 ease-in-out ${
                    markAsProblem
                      ? "max-h-44 opacity-100 scale-100"
                      : "max-h-0 opacity-0 scale-95 py-0"
                  }`}
                >
                  <Textarea
                    label={t("tickets.card.problemDetails")}
                    autoFocus
                    onClick={(e) => {
                      e.stopPropagation();
                    }}
                    placeholder={t("tickets.card.problemPlaceholder")}
                    {...form.getInputProps("problem_details")}
                  />
                </div>

                <div className="w-full flex transition-all duration-300 space-x-2">
                  {/* Clear Button */}
                  <div
                    className={`transition-all duration-300 ease-in-out overflow-hidden ${
                      markAsProblem
                        ? "opacity-100 scale-100 basis-1/2"
                        : "opacity-0 scale-95 basis-0 pointer-events-none !m-0 !p-0 !w-0"
                    }`}
                  >
                    <Button
                      color="gray"
                      variant="outline"
                      fullWidth
                      leftSection={<IconX size={18} />}
                      onClick={(e) => {
                        e.stopPropagation();
                        setMarkAsProblem(false);
                      }}
                    >
                      {t("common.clear")}
                    </Button>
                  </div>

                  {/* Submit / Mark Button */}
                  <div
                    className={`transition-all duration-400 ease-in-out ${
                      markAsProblem ? "basis-1/2" : "basis-full"
                    }`}
                  >
                    <PermissionTooltip disabled={!canWrite()}>
                      <Button
                        color={markAsProblem ? "green" : "red"}
                        variant="outline"
                        size="xs"
                        fullWidth
                        leftSection={
                          markAsProblem && <IconDeviceFloppy size={18} />
                        }
                        loading={isUpdating}
                        disabled={!canWrite()}
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!markAsProblem) {
                            setMarkAsProblem(true);
                          } else {
                            form.onSubmit(handleSubmit)();
                          }
                        }}
                      >
                        {markAsProblem
                          ? t("common.save")
                          : ticket.status === TicketStatus.ProblemTickets
                            ? t("tickets.card.updateReason")
                            : t("tickets.card.markAsProblem")}
                      </Button>
                    </PermissionTooltip>
                  </div>
                </div>
              </form>
            </div>
          </div>

          <div className="px-3 w-full flex items-center h-fit justify-between border-t border-t-gray-200 pt-2">
            <span className="text-2xs font-bold uppercase text-gray-500">
              {expanded ? t("common.hideDetails") : t("common.showDetails")}
            </span>
            <ActionButton
              color="gray"
              variant={expanded ? "filled" : "outline"}
              icon={IconChevronDown}
              size={"xs"}
              className={`m-0 p-0 transition-all duration-300 ${
                expanded ? "rotate-180" : "rotate-0"
              }`}
            />
          </div>
        </div>
      </Card>
    </div>
  );
};
